import {
  AgxSpacer,
  AgxTypography,
  dateOnlyToDate,
  toDateOnlyString,
  toInclusiveDate,
} from '@urbanx/agx.ui-components2';
import { useEffect } from 'react';
import { allowedCommissionTypes } from 'pages/finance/commission/commission/CommissionEnums';
import AgreementCommissionStageSteps from 'pages/campaigns/components/campaignTasks/AgreementCompliance/AgreementCommissionStageSteps';
import AppointmentPeriod from 'pages/campaigns/components/campaignTasks/AgreementCompliance/NSW/AppointmentPeriod';
import { updateAgreementDetailsAppointmentPeriod } from 'pages/campaigns/components/campaignTasks/AgreementCompliance/NSW/appointmentPeriodAgreementDetails';
import { AgreementCompliancePageProps } from 'pages/allTasks/TaskModal/AgreementCompliancePageProps';

const toDisplayEndDate = (endDateExclusive?: Date | string | null) =>
  endDateExclusive ? (dateOnlyToDate(toInclusiveDate(endDateExclusive)) ?? undefined) : undefined;

const toDisplayDate = (date?: Date | string | null) =>
  date ? (dateOnlyToDate(toDateOnlyString(date)) ?? undefined) : undefined;

const NswAgreementComplianceTypePage = ({
  task,
  taskNumber,
  toggleTaskStep,
  agreementDetails,
  onAgreementDetailsUpdated,
  commissionDetails,
  currentCommissionInfo,
  onCommissionUpdated,
  registerTaskModalPageValidation,
}: AgreementCompliancePageProps) => {
  const appointmentStartStep = task.taskSteps?.find((step) => step.order === 10);
  const appointmentEndStep = task.taskSteps?.find((step) => step.order === 11);
  const commissionStep = task.taskSteps?.find((step) => step.order === 12);

  if (!appointmentStartStep || !appointmentEndStep || !commissionStep) {
    throw new Error('NSW Agreement Compliance appointment or commission steps are missing');
  }

  useEffect(() => {
    if (!agreementDetails) return;

    const dates = [Boolean(agreementDetails.startDate), Boolean(agreementDetails.endDateExclusive)];

    [appointmentStartStep, appointmentEndStep].forEach((step, index) => {
      if (step.isCompleted !== dates[index]) {
        toggleTaskStep(step.order ?? 0, dates[index]);
      }
    });
  }, [agreementDetails, appointmentEndStep, appointmentStartStep, toggleTaskStep]);

  if (!agreementDetails || !onAgreementDetailsUpdated || !onCommissionUpdated) {
    return null;
  }

  return (
    <AgxSpacer direction="vertical" gap="spacing-3xl" fill>
      <AgxTypography size="large" type="label" id="step2title" color="var(--text-primary)">
        {`Step ${taskNumber}: Appointment Dates & Commission`}
      </AgxTypography>
      <AgxTypography size="medium" type="title" id="modalTitle-appointment-period">
        Appointment Period
      </AgxTypography>
      <AppointmentPeriod
        value={{
          startDate: toDisplayDate(agreementDetails.startDate),
          endDate: toDisplayEndDate(agreementDetails.endDateExclusive),
        }}
        onChange={(updated) => {
          onAgreementDetailsUpdated({
            id: 'period',
            value: updateAgreementDetailsAppointmentPeriod(agreementDetails, updated),
          });
        }}
      />
      <AgreementCommissionStageSteps
        commissionDetails={commissionDetails}
        onCommissionUpdated={onCommissionUpdated}
        allowedCommissionTypes={allowedCommissionTypes}
        currentCommissionInfo={currentCommissionInfo}
        commissionStep={commissionStep}
        updateCommissionTaskStep={(isCompleted) =>
          toggleTaskStep(commissionStep.order ?? 0, isCompleted)
        }
        registerTaskModalPageValidation={registerTaskModalPageValidation}
      />
    </AgxSpacer>
  );
};

export default NswAgreementComplianceTypePage;
